import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'

import {
  futuresAccessDialogActionsSx,
  futuresAccessDialogContentSx,
  futuresAccessDialogPaperProps,
  futuresAccessDialogTitleSx,
} from '../pages/futures/futuresAccessUi'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  /** Popup kiểu form Access (Futures). */
  accessChrome?: boolean
  /** Nội dung xác nhận (ưu tiên thấp hơn `message` nếu có) */
  children?: ReactNode
  /** Chuỗi nội dung — tương thích code cũ dùng `message` thay vì `children` */
  message?: ReactNode
  confirmLabel?: string
  /** @deprecated Dùng `confirmLabel` */
  confirmText?: string
  cancelLabel?: string
  /** Nút xác nhận màu error (xoá / thao tác nguy hiểm) */
  danger?: boolean
  /** @deprecated Dùng `danger` */
  destructive?: boolean
  /** Vô hiệu hoá nút xác nhận (đang gọi API) */
  loading?: boolean
  /** Đóng dialog (huỷ / backdrop). */
  onClose?: () => void
  /** Huỷ — tương thích consumer cũ; gọi cùng lúc với `onClose` khi cả hai có. */
  onCancel?: () => void
  onConfirm: () => void | Promise<void>
}

/**
 * Dialog xác nhận thay cho `window.confirm` — đồng bộ theme MUI.
 */
export default function ConfirmDialog({
  open,
  title,
  children,
  message,
  confirmLabel,
  confirmText,
  cancelLabel = 'Cancel',
  danger,
  destructive,
  loading,
  onClose,
  onCancel,
  onConfirm,
  accessChrome,
}: ConfirmDialogProps) {
  const body = message ?? children
  const confirmWording = confirmLabel ?? confirmText ?? 'Confirm'
  const isDanger = danger ?? destructive ?? false

  const handleDismiss = () => {
    onCancel?.()
    onClose?.()
  }

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="xs"
      fullWidth
      PaperProps={accessChrome ? futuresAccessDialogPaperProps : undefined}
    >
      <DialogTitle sx={accessChrome ? futuresAccessDialogTitleSx : undefined}>{title}</DialogTitle>
      <DialogContent dividers={accessChrome} sx={accessChrome ? futuresAccessDialogContentSx : undefined}>
        {body == null || body === false ? null : typeof body === 'string' ? (
          <Typography variant="body2" color={accessChrome ? undefined : 'text.secondary'}>
            {body}
          </Typography>
        ) : (
          body
        )}
      </DialogContent>
      <DialogActions sx={accessChrome ? futuresAccessDialogActionsSx : undefined}>
        <Button onClick={handleDismiss} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          color={isDanger ? 'error' : 'primary'}
          variant="contained"
          disabled={loading}
          onClick={() =>
            void Promise.resolve(onConfirm()).then(() => {
              onClose?.()
            })
          }
        >
          {confirmWording}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
